import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";

const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/i;

const parseEmails = value =>
  (value || "")
    .split(/[,;]+/)
    .map(email => email.trim())
    .filter(Boolean);

const uniqueEmails = emails => emails.filter((email, index) =>
  emails.findIndex(candidate => candidate.toLowerCase() === email.toLowerCase()) === index
);

const invalidEmails = emails => emails.filter(email => !EMAIL_REGEX.test(email));

const formatList = emails => (emails.length > 0 ? emails.join(", ") : "None configured");

export default function EmailBillingReportModal({
  open,
  report,
  projectName,
  projectBillingEmailSendList,
  companyEmails,
  clientBillingEmails,
  onClose,
  onSend,
  onViewReport,
  allowEditProjectRecipients = true,
}) {
  const [editableValue, setEditableValue] = useState(projectBillingEmailSendList || "");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setEditableValue(projectBillingEmailSendList || "");
      setSubmitting(false);
    }
  }, [open, projectBillingEmailSendList]);

  const projectEmails = useMemo(() => uniqueEmails(parseEmails(editableValue)), [editableValue]);
  const companyList = useMemo(() => uniqueEmails(companyEmails || []), [companyEmails]);
  const clientList = useMemo(() => uniqueEmails(clientBillingEmails || []), [clientBillingEmails]);
  const projectInvalid = useMemo(() => invalidEmails(parseEmails(editableValue)), [editableValue]);

  const toRecipients = companyList;
  const ccRecipients = useMemo(
    () =>
      uniqueEmails([...projectEmails.filter(email => EMAIL_REGEX.test(email)), ...clientList]).filter(
        email => !toRecipients.some(toEmail => toEmail.toLowerCase() === email.toLowerCase())
      ),
    [clientList, projectEmails, toRecipients]
  );
  const allRecipients = useMemo(() => uniqueEmails([...toRecipients, ...ccRecipients]), [toRecipients, ccRecipients]);

  const canSend = projectInvalid.length === 0 && allRecipients.length > 0 && toRecipients.length > 0;
  const helperText = projectInvalid.length > 0
    ? `Invalid emails: ${projectInvalid.join(", ")}`
    : allowEditProjectRecipients
    ? "Separate multiple emails with commas."
    : "Project billing recipients are read-only for this role.";

  const handleSend = async () => {
    setSubmitting(true);
    try {
      await onSend({
        billingReportId: report.id,
        ...(allowEditProjectRecipients && { projectBillingEmailSendList: editableValue }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewClick = () => {
    if (report && onViewReport) {
      onViewReport(report);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", color: "black", fontSize: 24 }}>
        Email billing report
      </DialogTitle>
      <DialogContent>
        <Box
          mb={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap"
        >
          <Typography variant="body1" color="text.secondary">
            {projectName} - Version {report?.version}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={handleViewClick}
            disabled={!report?.reportUrl || submitting}
          >
            View
          </Button>
        </Box>

        {!canSend && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {toRecipients.length === 0
              ? "At least one valid company email is required before this report can be sent."
              : "At least one valid recipient is required before this report can be sent."}
          </Alert>
        )}

        <TextField
          label="Project billing send list (CC)"
          fullWidth
          multiline
          minRows={3}
          value={editableValue}
          onChange={event => setEditableValue(event.target.value)}
          error={projectInvalid.length > 0}
          helperText={helperText}
          disabled={!allowEditProjectRecipients}
        />

        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Final recipients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatList(allRecipients)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 1 }}>
        <Box display="flex" gap={1}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSend} variant="contained" disabled={!canSend || submitting}>
            {submitting ? <CircularProgress size={18} color="inherit" /> : "Send"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
